import React from 'react';
import { Clock, Sun, Moon, Coffee, Utensils } from 'lucide-react';

function OperatingHours({ settings, t }) {
  const schedule = {
    morningStart: settings?.morningStart || "08:00",
    morningEnd: settings?.morningEnd || "12:00",
    afternoonStart: settings?.afternoonStart || "13:30",
    afternoonEnd: settings?.afternoonEnd || "17:30",
    break1Start: settings?.break1Start || "10:00",
    break2Start: settings?.break2Start || "16:00",
    breakDuration: settings?.breakDuration || 10,
  };

  // Calculate total useful time
  const parseTime = (t) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  const morningMin = parseTime(schedule.morningEnd) - parseTime(schedule.morningStart);
  const afternoonMin = parseTime(schedule.afternoonEnd) - parseTime(schedule.afternoonStart);
  const lunchMin = parseTime(schedule.afternoonStart) - parseTime(schedule.morningEnd);
  const breaksMin = schedule.breakDuration * 2;
  const totalUsefulMin = morningMin + afternoonMin - breaksMin;
  const totalUsefulHours = Math.floor(totalUsefulMin / 60);
  const totalUsefulRemainMin = totalUsefulMin % 60;

  // Timeline segments (8h to 18h = 600 minutes = 100%)
  const timelineStart = 8 * 60; // 08:00
  const timelineEnd = 18 * 60;  // 18:00
  const timelineWidth = timelineEnd - timelineStart;

  const getPosition = (timeStr) => {
    const min = parseTime(timeStr);
    return ((min - timelineStart) / timelineWidth) * 100;
  };

  const getWidth = (startStr, endStr) => {
    return getPosition(endStr) - getPosition(startStr);
  };

  const timeBlocks = [
    { 
      label: 'MANHÃ', 
      time: `${schedule.morningStart} - ${schedule.morningEnd}`, 
      duration: `${Math.floor(morningMin / 60)}h`,
      icon: Sun,
      color: 'sky'
    },
    { 
      label: 'TARDE', 
      time: `${schedule.afternoonStart} - ${schedule.afternoonEnd}`, 
      duration: `${Math.floor(afternoonMin / 60)}h`,
      icon: Moon,
      color: 'indigo'
    },
    { 
      label: 'PAUSAS', 
      time: `${schedule.break1Start} e ${schedule.break2Start}`, 
      duration: `2 x ${schedule.breakDuration} min`,
      icon: Coffee,
      color: 'amber'
    },
    { 
      label: 'ALMOÇO', 
      time: `${schedule.morningEnd} - ${schedule.afternoonStart}`, 
      duration: `${Math.floor(lunchMin / 60)}h${lunchMin % 60 > 0 ? (lunchMin % 60) + 'min' : ''}`,
      icon: Utensils,
      color: 'gray'
    },
  ];

  const hours = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

  return (
    <div className={`${t.card} rounded-lg p-6`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-sky-500" />
          <h3 className={`text-sm font-semibold ${t.text} uppercase tracking-wider`} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            Horário de Funcionamento
          </h3>
        </div>
        <span className={`text-sm ${t.textSec}`}>
          Tempo Útil: <span className="font-semibold">{totalUsefulHours}h{totalUsefulRemainMin > 0 ? totalUsefulRemainMin : ''}</span> ({totalUsefulMin} min)
        </span>
      </div>

      {/* Time blocks */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {timeBlocks.map((block, idx) => {
          const Icon = block.icon;
          const colorMap = {
            sky: 'bg-sky-500/10 border-sky-500/20 text-sky-500',
            indigo: 'bg-indigo-500/10 border-indigo-500/20 text-indigo-500',
            amber: 'bg-amber-500/10 border-amber-500/20 text-amber-500',
            gray: 'bg-gray-500/10 border-gray-500/20 text-gray-400',
          };
          
          return (
            <div key={idx} className={`p-3 rounded-lg border ${t.inner}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-6 h-6 rounded-md flex items-center justify-center border ${colorMap[block.color]}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-medium ${t.textMut}`}>{block.label}</span>
              </div>
              <div className={`text-base font-semibold ${t.text}`}>{block.time}</div>
              <div className={`text-xs ${t.textMut}`}>{block.duration}</div>
            </div>
          );
        })}
      </div>

      {/* Visual Timeline */}
      <div className="relative">
        {/* Hour markers */}
        <div className="flex justify-between text-[10px] text-gray-400 mb-1">
          {hours.map(h => (
            <span key={h}>{h.toString().padStart(2, '0')}h</span>
          ))}
        </div>
        
        {/* Timeline bar */}
        <div className={`relative h-8 ${t.inner} rounded-full overflow-hidden`}>
          {/* Morning work */}
          <div 
            className="absolute h-full bg-sky-500 rounded-l-full"
            style={{ 
              left: `${getPosition(schedule.morningStart)}%`, 
              width: `${getWidth(schedule.morningStart, schedule.break1Start)}%` 
            }}
          />
          {/* Break 1 */}
          <div 
            className="absolute h-full bg-amber-500"
            style={{ 
              left: `${getPosition(schedule.break1Start)}%`, 
              width: `${(schedule.breakDuration / timelineWidth) * 100}%` 
            }}
          />
          {/* Morning work after break */}
          <div 
            className="absolute h-full bg-sky-500"
            style={{ 
              left: `${getPosition(schedule.break1Start) + (schedule.breakDuration / timelineWidth) * 100}%`, 
              width: `${getWidth(schedule.break1Start, schedule.morningEnd) - (schedule.breakDuration / timelineWidth) * 100}%` 
            }}
          />
          {/* Lunch */}
          <div 
            className="absolute h-full bg-gray-400"
            style={{ 
              left: `${getPosition(schedule.morningEnd)}%`, 
              width: `${getWidth(schedule.morningEnd, schedule.afternoonStart)}%` 
            }}
          />
          {/* Afternoon work before break */}
          <div 
            className="absolute h-full bg-sky-500"
            style={{ 
              left: `${getPosition(schedule.afternoonStart)}%`, 
              width: `${getWidth(schedule.afternoonStart, schedule.break2Start)}%` 
            }}
          />
          {/* Break 2 */}
          <div 
            className="absolute h-full bg-amber-500"
            style={{ 
              left: `${getPosition(schedule.break2Start)}%`, 
              width: `${(schedule.breakDuration / timelineWidth) * 100}%` 
            }}
          />
          {/* Afternoon work after break */}
          <div 
            className="absolute h-full bg-sky-500 rounded-r-full"
            style={{ 
              left: `${getPosition(schedule.break2Start) + (schedule.breakDuration / timelineWidth) * 100}%`, 
              width: `${getWidth(schedule.break2Start, schedule.afternoonEnd) - (schedule.breakDuration / timelineWidth) * 100}%` 
            }}
          />
        </div>
        
        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-sky-500 rounded-full" />
            <span className={`text-xs ${t.textMut}`}>Trabalho ({totalUsefulHours}h{totalUsefulRemainMin > 0 ? totalUsefulRemainMin : ''})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-amber-500 rounded-full" />
            <span className={`text-xs ${t.textMut}`}>Pausas ({breaksMin} min)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gray-400 rounded-full" />
            <span className={`text-xs ${t.textMut}`}>Almoço ({Math.floor(lunchMin / 60)}h{lunchMin % 60 > 0 ? (lunchMin % 60) : ''})</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OperatingHours;
